import React, { forwardRef, useEffect, useImperativeHandle, useState, useRef } from 'react';
import Editor from '@draft-js-plugins/editor';
import { EditorState, Modifier, SelectionState, CompositeDecorator } from 'draft-js';
import { stateToHTML } from "draft-js-export-html";
import { stateFromHTML } from 'draft-js-import-html';
import createToolbarPlugin from '@draft-js-plugins/static-toolbar';
import {
	ItalicButton,
	BoldButton,
	UnderlineButton,
	UnorderedListButton,
	OrderedListButton,
	BlockquoteButton,
	CodeBlockButton,
} from '@draft-js-plugins/buttons';
import editorStyles from './Editor.module.css';

const insertFlashDecorator = {
	strategy(block, callback) {
		block.findStyleRanges(
			(character) => character.getStyle().has('INSERT_FLASH'),
			callback
		);
	},
	component: ({ children }) => (
		<span className={editorStyles.insertFlash}>{children}</span>
	),
};

const createDecorator = () => new CompositeDecorator([insertFlashDecorator]);

const createEditorStateFromContent = (contentState) => (
	contentState
		? EditorState.createWithContent(contentState, createDecorator())
		: EditorState.createEmpty(createDecorator())
);

const clearInsertFlash = (state) => {
	let content = state.getCurrentContent();

	content.getBlockMap().forEach((block) => {
		block.findStyleRanges(
			(character) => character.getStyle().has('INSERT_FLASH'),
			(start, end) => {
				const selection = SelectionState.createEmpty(block.getKey()).merge({
					anchorOffset: start,
					focusOffset: end,
				});
				content = Modifier.removeInlineStyle(content, selection, 'INSERT_FLASH');
			}
		);
	});

	return EditorState.set(state, { currentContent: content });
};

const CustomEditor = forwardRef((props, ref) => {
    const [editorState, setEditorState] = useState(() => {
        if (props.defaultValue) {
            const contentState = stateFromHTML(props.defaultValue);
            return createEditorStateFromContent(contentState);
        }
        return createEditorStateFromContent(null);
    });
	const editorRef = useRef();
	const lastSelectionRef = useRef(null);
	const editorStateRef = useRef(editorState);
	const flashTimeoutRef = useRef(null);
	const [{ plugins, Toolbar }] = useState(() => {
		const toolbarPlugin = createToolbarPlugin();
		const { Toolbar } = toolbarPlugin;
		const plugins = [toolbarPlugin];
		return {
			plugins,
			Toolbar
		};
	});

	editorStateRef.current = editorState;

	useImperativeHandle(ref, () => ({
		getText: () => {
			return editorState.getCurrentContent().getPlainText();
		},
		getHTML: () => {
			return stateToHTML(editorState.getCurrentContent())
		},
		focus: () => {
			editorRef.current?.focus();
		},
		setHTML: (html) => {
			if (flashTimeoutRef.current) {
				clearTimeout(flashTimeoutRef.current);
				flashTimeoutRef.current = null;
			}
			const contentState = stateFromHTML(html || '<p></p>');
			const newEditorState = createEditorStateFromContent(contentState);
			lastSelectionRef.current = newEditorState.getSelection();
			setEditorState(newEditorState);
		},
		insertText: (text, options = {}) => {
			if (!text) {
				return;
			}

			let currentState = editorState;
			if (options.flash) {
				if (flashTimeoutRef.current) {
					clearTimeout(flashTimeoutRef.current);
					flashTimeoutRef.current = null;
				}
				currentState = clearInsertFlash(currentState);
			}

			const contentState = currentState.getCurrentContent();
			let selection = currentState.getSelection();

			if (!selection.getHasFocus()) {
				const savedSelection = lastSelectionRef.current ?? selection;
				if (contentState.getBlockForKey(savedSelection.getAnchorKey())) {
					selection = savedSelection;
				} else {
					const lastBlock = contentState.getBlockMap().last();
					const lastKey = lastBlock.getKey();
					const lastLength = lastBlock.getLength();
					selection = SelectionState.createEmpty(lastKey).merge({
						anchorOffset: lastLength,
						focusOffset: lastLength,
					});
				}
			}

			let newContentState = Modifier.insertText(contentState, selection, text);
			const selectionAfter = newContentState.getSelectionAfter();
			const blockKey = selectionAfter.getFocusKey();
			const end = selectionAfter.getFocusOffset();

			if (options.flash) {
				const start = end - text.length;
				const insertedRange = SelectionState.createEmpty(blockKey).merge({
					anchorOffset: start,
					focusOffset: end,
				});
				newContentState = Modifier.applyInlineStyle(newContentState, insertedRange, 'INSERT_FLASH');
			}

			const collapsedSelection = SelectionState.createEmpty(blockKey).merge({
				anchorOffset: end,
				focusOffset: end,
				hasFocus: false,
			});

			const newEditorState = EditorState.push(currentState, newContentState, 'insert-characters');
			lastSelectionRef.current = collapsedSelection;
			setEditorState(EditorState.forceSelection(newEditorState, collapsedSelection));

			if (options.flash) {
				flashTimeoutRef.current = setTimeout(() => {
					setEditorState((state) => clearInsertFlash(editorStateRef.current));
					flashTimeoutRef.current = null;
				}, 2200);
			}
		},
		reset: () => {
			if (flashTimeoutRef.current) {
				clearTimeout(flashTimeoutRef.current);
				flashTimeoutRef.current = null;
			}
			lastSelectionRef.current = null;
			setEditorState(createEditorStateFromContent(null));
		}
	}));

	useEffect(() => () => {
		if (flashTimeoutRef.current) {
			clearTimeout(flashTimeoutRef.current);
		}
	}, []);

	const onChange = (nextEditorState) => {
		lastSelectionRef.current = nextEditorState.getSelection();
		setEditorState(nextEditorState);
		props.onChange?.();
	};

	const focus = () => {
		editorRef.current.focus();
	};

	const isEmpty = editorState.getCurrentContent().hasText();

	return (
		<div
			className={`${editorStyles.editor} ${props.comfortable ? editorStyles.editorComfortable : ''}`.trim()}
			onClick={focus}
			data-invalid={props.isInvalid}
		>
			{!isEmpty && <div className={editorStyles.placeholder}>{props.placeholder}</div>}
			<Editor
				editorState={editorState}
				onChange={onChange}
				plugins={plugins}
				ref={editorRef}
			/>
			<div className={editorStyles.toolbar}>
				<Toolbar>
					{
						(externalProps) => (
							<div className={editorStyles.buttonsContainer}>
								<BoldButton {...externalProps} />
								<ItalicButton {...externalProps} />
								<UnderlineButton {...externalProps} />
								<UnorderedListButton {...externalProps} />
								<OrderedListButton {...externalProps} />
								<BlockquoteButton {...externalProps} />
								<CodeBlockButton {...externalProps} />
							</div>
						)
					}
				</Toolbar>
			</div>
		</div>
	);
});

export default CustomEditor;